/**
 * unitTelemetry — telemetry-lite beacon queue for the match-page deck
 * (content-portfolio Phase 3).
 *
 * Collects anonymous per-unit actions ('impression' | 'odds_click' |
 * 'expand') on the ranked deck and flushes them in batches to the
 * engine's fail-soft POST /feed/:slug/unit-events route (the URL is
 * built by the caller from the tenant's engine endpoints, so it follows
 * the same Caddy-proxied base every other feed call uses).
 *
 * Design rules (owner mandate: SIMPLE, no vendors, no user-level data):
 *   - Module-level singleton, framework-free. One page view at a time —
 *     exactly the match-detail deck's shape.
 *   - Dedupe per (kind, ref, action) per page view: scrolling a unit in
 *     and out ten times is ONE impression.
 *   - Flush via navigator.sendBeacon (survives navigation); fallback to
 *     fetch with keepalive. Batches respect the route's 50-unit cap.
 *   - FAIL-SOFT IS THE CONTRACT: every network touch is wrapped; a lost
 *     beacon must never throw into the page. Telemetry is the one place
 *     where silent failure is the design (see house rules).
 */

export type UnitEventAction = 'impression' | 'odds_click' | 'expand';

interface QueuedUnitEvent {
  kind: string;
  ref: string;
  action: UnitEventAction;
}

/** Engine route contract: max units per beacon body. */
const MAX_UNITS_PER_BEACON = 50;
/** Small debounce so a burst of impressions on load flushes as ONE beacon. */
const FLUSH_DELAY_MS = 3000;

let endpointUrl: string | null = null;
let pageEventId: string | null = null;
let seen = new Set<string>();
let queue: QueuedUnitEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lifecycleHooked = false;

function hookLifecycleOnce(): void {
  if (lifecycleHooked || typeof window === 'undefined') return;
  lifecycleHooked = true;
  // pagehide + visibility→hidden are the recommended "last chance to
  // beacon" moments (unload is unreliable on mobile). Registered once
  // for the app's lifetime — they no-op when the queue is empty.
  window.addEventListener('pagehide', () => flushUnitEvents());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushUnitEvents();
  });
}

/**
 * Start (or restart) a telemetry page view. Idempotent for the same
 * (url, eventId) pair so callers may invoke it during render (before any
 * child records) without StrictMode double-invocation side effects. A
 * NEW pair flushes whatever the previous page still had queued, then
 * resets the dedupe set.
 */
export function beginUnitTelemetryPageView(opts: { url: string; eventId: string }): void {
  if (endpointUrl === opts.url && pageEventId === opts.eventId) return;
  flushUnitEvents();
  endpointUrl = opts.url;
  pageEventId = opts.eventId;
  seen = new Set<string>();
  queue = [];
  hookLifecycleOnce();
}

/**
 * Record one unit action. No-op before beginUnitTelemetryPageView and
 * for any (kind, ref, action) already recorded this page view.
 */
export function recordUnitEvent(kind: string, ref: string, action: UnitEventAction): void {
  if (endpointUrl === null || pageEventId === null) return;
  if (!kind || !ref) return;
  const key = `${kind} ${ref} ${action}`;
  if (seen.has(key)) return;
  seen.add(key);
  queue.push({ kind, ref, action });
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushUnitEvents();
    }, FLUSH_DELAY_MS);
  }
}

/** Send everything queued now (used on pagehide/hidden + page-view swap). */
export function flushUnitEvents(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (endpointUrl === null || pageEventId === null || queue.length === 0) return;
  const url = endpointUrl;
  const eventId = pageEventId;
  while (queue.length > 0) {
    const batch = queue.splice(0, MAX_UNITS_PER_BEACON);
    sendBatch(url, JSON.stringify({ eventId, units: batch }));
  }
}

function sendBatch(url: string, body: string): void {
  // sendBeacon first — it survives navigation and never blocks paint.
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
  } catch {
    // fall through to fetch
  }
  try {
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* fail-soft: a lost beacon never surfaces */
    });
  } catch {
    /* fail-soft: even a synchronous fetch throw (no fetch in env) is swallowed */
  }
}

/** Test seam — drops all module state (page view, queue, dedupe). */
export function _resetUnitTelemetryForTest(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  endpointUrl = null;
  pageEventId = null;
  seen = new Set<string>();
  queue = [];
}
